import React, { useEffect, useRef, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import QuestionContainer from './QuestionContainer'
import { Field, change, formValueSelector, touch } from 'redux-form'
import FocusTrap from 'focus-trap-react'
import { required } from '../../../../utilties/validators'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import { useIntl } from 'react-intl'
import { useSyncRefHeight } from '../../../../utilties/utilities'

const requiredValidator = required

const FormControlLabelCustom = ({ showError, input, value, i, label, meta: { touched, invalid, error }, ...rest }) => {
	const itemsRef = useRef(null)

	
	useSyncRefHeight(
		[
			[`single_items_${i}`, itemsRef],
		],
		[ i ],
	)
	
	
	return (
		<Grid item container ref={itemsRef} style={{ paddingBottom: '10px' }}>
			<Grid item xs={12}>
				<FormControlLabel
					data-testid="radio-select-question"
					key={i}
					style={{ color: 'black' }}
					error={touched && invalid}
					helperText={touched && error}
					control={
						<Radio color="primary"
							data-testid="radio-select-question-radio"
							value={i.toString()} style={{ alignSelf: 'flex-start', padding: '5px', marginRight: '5px' }} />
					}
					labelPlacement='end'
					label={
						<Typography
							data-testid="radio-select-question-label"
							variant='subtitle1'
							style={{ fontSize: '17px', fontWeight: '400', lineHeight: '24px' }}>
							{label}
						</Typography>
					}
					checked={input.value === i.toString()}
					{...input}
					{...rest}
				/>
			</Grid>
			{showError && touched && error && 
				<Grid item xs={12} style={{paddingLeft: '8px'}}><span data-testid="radio-select-question-error" className="errorWrapper">{error}</span></Grid>
			}
		</Grid>
	)
}

const FormRadioGroup = ({ question, value, meta: { touched, invalid, error }, onQuestionValueChanged, disabled, inputName, ...rest }) => {
	const name = inputName ?? `answers.single._${question.uniqueId}`

	let validators = question.isOptional ? null : requiredValidator

	const [ trapIsActive, setTrapIsActive ] = useState(false)

	useEffect(() => {
		if (document.getElementById(name) !== null) {
			document.getElementById(name).addEventListener('keydown', (e)=>{
				if (e.keyCode === 13) {
					e.preventDefault()
					setTrapIsActive(true)
				}
				if (e.keyCode === 27) {
					e.preventDefault()
					setTrapIsActive(false)
				}
			})
		}
	}, [])

	const handleChange = (answer, value) => {
		question.answers.find((a) => {
			if (
				a.id !== answer.id
				&& !!a.checked
			)
			{
				onQuestionValueChanged(question, false, a, undefined)
			}
		})
		onQuestionValueChanged(question, true, answer, undefined)
	}

	return (
		<FormControl
			component='fieldset'
			data-testid="radio-group-form-control"
			className="container-radiogroup-grid"
			style={{
				marginLeft: '-6px'
			}}>
			<FormGroup>
				<FocusTrap
					active={trapIsActive}
					focusTrapOptions={{
						returnFocusOnDeactivate: false
					}}>
					<RadioGroup data-testid="radio-group" id={name}>
						{question &&
							question.answers &&
							question.answers.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1)).map((r, i) => {
								return (
									<Field
										data-testid="radio-group-field"
										component={FormControlLabelCustom}
										disabled={disabled}
										i={r.id}
										label={r.label}
										name={name}
										value={value}
										validate={validators}
										showError={i == question.answers.length - 1 ? true : false}
										onChange={e => handleChange(r, e.target.value)}
										onBlur={e => handleChange(r, e.target.value)}
									/>
								)
							})}
					</RadioGroup>
				</FocusTrap>
				{touched && error && <span data-testid="radio-group-error" className="errorWrapper">{error}</span>}
			</FormGroup>
		</FormControl>
	)
}

const FormControlLabelDropdown = ({ showError, input: { value }, onValueChange, question, check, label, disabled, meta: { touched, invalid, error }, ...rest }) => {
	const intl = useIntl()

	const filterOptions = (
		candidate,
		input
	) => {
		if (candidate && input) {
			return candidate?.label.toLowerCase().includes(input.toLowerCase())
		}
		return true
	}

	return (
		<Grid item container data-testid="writable-radio-custom">
			<Grid item xs={12}>
				<FormControlLabel
					style={{ display: 'block' }}
					data-testid="writable-radio-label-wrap"
					error={touched && invalid}
					helperText={touched && error}
					control={
						<Select
						classNamePrefix='react-select-styled'
						noOptionsMessage={() => null}
						menuPosition="fixed"
						menuPortalTarget={document.body} 
						styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
						value={value ? question.answers.find((a) => a.id === Number.parseInt(value)) : null}
						onChange={onValueChange}
						options={question.answers.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))}
						getOptionValue={option => option.id}
						isDisabled={disabled}
						filterOption={filterOptions}
						placeholder={intl.formatMessage({id: 'app.survey.question.dropdownPlaceholder'})}
					/>
					}
					labelPlacement='end'
					label={
						<Typography data-testid="writable-radio-label" variant='body2' style={{ fontSize: '17px', lineHeight: '24px' }}>
							{label}
						</Typography>
					}
                    checked={check}
					{...rest}
				/>
			</Grid>
			{showError && touched && error && 
				<Grid item xs={12}><span data-testid="writable-radio-error" className="errorWrapper">{error}</span></Grid>
			}
		</Grid>
	)
}

const FormDropdown = ({ question, currentValue, check, meta: { touched, invalid, error, form, dispatch }, val, onValueChange, onQuestionValueChanged, disabled, inputName }) => {
	const name = inputName ?? `answers.single._${question.uniqueId}`

	let validators = question.isOptional ? null : requiredValidator

    const onChangeHandler = (answer) => {
		dispatch(touch(form, name))
        if (answer) {
            dispatch(change(form, name, answer.id))
			if(onValueChange){
				onValueChange(answer.id)
			}
			if (Number.parseInt(currentValue) > 0) {
				onQuestionValueChanged(question, false, question.answers.find((a) => a.id === Number.parseInt(currentValue)))
			}
			onQuestionValueChanged(question, true, answer, undefined)
		}
	}

    useEffect(
		() => {
            let answer = question.answers.find(e => e.checked);
			if (answer) {
                dispatch(change(form, name, answer.id));
				if(onValueChange){
					onValueChange(answer.id)
				}
            }
        },
		[]
    )

	return (
		<FormControl
			data-testid="writable-radio-formcontrol"
			component='fieldset'
			className="form-control select-component"
			style={{
				marginLeft: '-5px'
			}}>
			<FormGroup data-testid="writable-radio-formgroup">
				<Field
					data-testid="writable-radio-field"
					disabled={disabled}
					component={FormControlLabelDropdown}
					name={name}
					value={currentValue}
					validate={validators}
					showError={true}
					onValueChange={onChangeHandler}
					question={question}
				/>
				{touched && error && <span data-testid="writable-radio-label-error" className="errorWrapper">{error}</span>}
			</FormGroup>
		</FormControl>
	)
}

export default ({
	question,
	position,
	positionDisplay,
	input,
	value,
	meta: { touched, invalid, error, form, dispatch },
	disabled,
	onQuestionValueChanged,
	isBilateral,
	isBilateralFullInfo,
	displayInstructionAsIcon,
	uniqueKey,
	...rest
}) => {
	const isLabelContainerPreserved = !!isBilateral && !!isBilateralFullInfo
	const name = `answers.single._${question.uniqueId}._${uniqueKey ?? ''}`

	const selector = formValueSelector(form)
	const currentValue = useSelector((state) => selector(state, name))

	const onRemoveAnswer = () => {
		question.answers.find((answer) => {
			if (answer.checked) {
				onQuestionValueChanged(question, false, answer, undefined)
				return true
			}
			else {
				return false
			}
		})

		dispatch(change(form, name, null))
	}

	return (
		<Grid>
			<QuestionContainer
				data-testid="radio-group-question-container"
				title={`${question.title}`} titleRequered = {`${question.isOptional ? '' : ' *'}`}
				position={position}
				positionDisplay={positionDisplay}
				instructions={question.instructions}
				canRemoveAnswer={question.isOptional && currentValue}
				onRemoveAnswer={onRemoveAnswer}
				isDropdown ={question.isDropdown ? true : false}
				formName={form}
				inputName={name}
				isBilateral={isBilateral}
				displayInstructionAsIcon={displayInstructionAsIcon}
				children={
					<Grid
						container
					>
						{
							!!isLabelContainerPreserved
							&& <Grid xs={6} />
						}
						<Grid
							item
							xs={isLabelContainerPreserved ? 6 : 12}
						>
							<Field
								data-testid="radio-question-container-radios"
								component={!question.isDropdown ? FormRadioGroup : FormDropdown}
								question={question}
								currentValue={currentValue}
								value={value}
								disabled={disabled}
								onQuestionValueChanged={onQuestionValueChanged}
								inputName={name}
								name={`field.radio.inner._${question.uniqueId}._${uniqueKey}`}
							/>
						</Grid>
					</Grid>
				}
			/>
		</Grid>
	)
}
